import { isOrganizationEventMessage, getEventData, getEventHeader } from './organization-events/eventParser';
import { isCalendarEventMessage, getCalendarEventData } from './calendar-events/calendarEventParser';
import { isBookingListMessage, getBookingListData } from './booking-list/bookingListParser';
import { isCancelBookingMessage, getCancelBookingData } from './cancel-booking/cancelBookingParser';
import { isUpdateBookingMessage, getUpdateBookingData } from './update-booking/updateBookingParser';
import {
  isCommunityAnnouncementMessage,
  getCommunityAnnouncementData,
} from './community-announcement/communityAnnouncementParser';
import {
  isOrganizationEventProactiveMessage,
  getProactiveEventHeader,
} from './organization-event-proactive/proactiveEventParser';

/**
 * Extract header + booking details, remove room list for web display.
 * Splits at the first numbered item (e.g. "\n\n1. ").
 */
export function parseRoomSelectionHeader(content) {
  if (!content) return content;
  const parts = content.split(/\n\n\d+\.\s/);
  return parts[0].trim();
}

/**
 * Given an assistant message, return the text the typewriter should type.
 *
 * - Room selection: header only (room list shown as cards)
 * - Organization events: header only (events shown as cards)
 * - Calendar events: empty string (cards handle everything)
 * - Booking list: viewHeader from raw_output (empty string if absent)
 * - Cancel booking: cancelHeader from raw_output
 * - Everything else: returns null (caller uses default content)
 */
export function getCardTypewriterText(message) {
  if (message.rooms?.length > 0) {
    return parseRoomSelectionHeader(message.content);
  }

  if (isOrganizationEventProactiveMessage(message)) {
    const header = getProactiveEventHeader(message);
    if (header !== null) return header;
  }

  if (isOrganizationEventMessage(message) && getEventData(message)) {
    return getEventHeader(message.content);
  }

  if (isCalendarEventMessage(message) && getCalendarEventData(message)) {
    return '';
  }

  if (isBookingListMessage(message)) {
    const data = getBookingListData(message);
    if (data) return data.viewHeader;
  }

  if (isCancelBookingMessage(message) && getCancelBookingData(message)) {
    return getCancelBookingData(message).cancelHeader;
  }

  if (isUpdateBookingMessage(message)) {
    const data = getUpdateBookingData(message);
    if (data) {
      // Confirmation state renders the header inside the card itself.
      if (data.conversationState === 'update_awaiting_confirmation') return '';
      // States with a dedicated card UI use the short updateHeader.
      if (data.conversationState === 'update_list_shown') return data.updateHeader;
      if (data.conversationState === 'update_collecting_fields') return data.updateHeader;
      // Other states (e.g. update_alt_time_shown, room selection) have no
      // card — fall through to default so the full output text is shown.
    }
  }

  // Community announcement: show the card's header (case A/B). Case C —
  // queued/status messages without ca_card — falls through to the default
  // content path so the full text renders normally.
  if (isCommunityAnnouncementMessage(message)) {
    const data = getCommunityAnnouncementData(message);
    if (data) return data.header;
  }

  return null; // not a card type — caller uses default content
}
